// src/pages/UserDownlineReport.js — Leader Report → Statistics (downline levels 1–6)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import LeaderReportListModal from "../components/LeaderReportListModal";
import toast from "react-hot-toast";
import { fetchLeaderReportStatistics } from "../services/leaderReportApi";
import {
    FaArrowLeft,
    FaSyncAlt,
    FaUserCircle,
    FaCalendarAlt,
    FaChartBar,
    FaCoins,
    FaMoneyBillWave,
    FaExchangeAlt,
    FaInfoCircle,
    FaListAlt,
    FaSearch,
    FaPhoneAlt,
    FaEnvelope,
    FaWallet,
    FaIdCard,
    FaExternalLinkAlt,
    FaFilePdf,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ----------------- Utils (aligned with LeaderStats) ----------------- */
function nfmt2(v) {
    const num = Number(v);
    if (!Number.isFinite(num)) return "0.00";
    return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
}

function cfmt(v) {
    const num = Number(v);
    if (!Number.isFinite(num)) return "0";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(num);
}

function toYMD(d = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

/** YYYY-MM-DD → DD/MM/YYYY (PDF display) */
function ymdToDdMmYyyy(ymd) {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return safeText(ymd);
    const [y, m, d] = ymd.split("-");
    return `${d}/${m}/${y}`;
}

/** YYYY-MM-DD → dd-mm-yyyy (PDF filename) */
function ymdToFilenameDate(ymd) {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "unknown-date";
    const [y, m, d] = ymd.split("-");
    return `${d}-${m}-${y}`;
}

function pdfMetricLabel(label) {
    return label === "Distinct Users" ? "User Count" : label;
}

function clampToMaxDate(ymd, maxYmd) {
    if (!ymd) return maxYmd;
    return ymd > maxYmd ? maxYmd : ymd;
}

function startOfWeekMonday(d) {
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = date.getDay();
    const diff = day === 0 ? 6 : day - 1;
    date.setDate(date.getDate() - diff);
    return date;
}

function getDatePresetRange(key) {
    const now = new Date();
    const today = toYMD(now);

    switch (key) {
        case "today":
            return { from: today, to: today };
        case "yesterday": {
            const y = new Date(now);
            y.setDate(y.getDate() - 1);
            const ymd = toYMD(y);
            return { from: ymd, to: ymd };
        }
        case "this_week": {
            const monday = startOfWeekMonday(now);
            return { from: toYMD(monday), to: today };
        }
        case "last_week": {
            const thisMonday = startOfWeekMonday(now);
            const lastSunday = new Date(thisMonday);
            lastSunday.setDate(lastSunday.getDate() - 1);
            const lastMonday = new Date(lastSunday);
            lastMonday.setDate(lastMonday.getDate() - 6);
            return { from: toYMD(lastMonday), to: toYMD(lastSunday) };
        }
        case "this_month": {
            const first = new Date(now.getFullYear(), now.getMonth(), 1);
            return { from: toYMD(first), to: today };
        }
        case "last_month": {
            const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const last = new Date(now.getFullYear(), now.getMonth(), 0);
            return { from: toYMD(first), to: toYMD(last) };
        }
        default:
            return { from: today, to: today };
    }
}

const DATE_PRESETS = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "this_week", label: "This Week" },
    { key: "last_week", label: "Last Week" },
    { key: "this_month", label: "This Month" },
    { key: "last_month", label: "Last Month" },
];

const METRIC_TOOLTIPS = {
    first_deposit:
        "Referral / first-deposit commission earned by the leader from downlines (levels 1–6).",
    normal_deposit: "Downline swaps USDT → coin in the selected period.",
    withdrawal: "Downline swaps coin → USDT in the selected period.",
    trade:
        "Downline trades in range: amount = volume, count = trades, win/loss = settled payout vs stake.",
};

/* ----------------- UI ----------------- */
/** @type {Record<string, import("../types/leaderReportList").LeaderReportListVariant>} */
const CARD_MODAL_VARIANT = {
    first_deposit: "first-deposit",
    normal_deposit: "normal-deposit",
    withdrawals: "withdrawal",
    withdrawal: "withdrawal",
    trade_amount: "trade",
    trade_win: "trade",
    trade_loss: "trade",
    trade_count: "trade",
    trade: "trade",
};

function DetailCard({ title, tone, icon, tooltip, subtitle, metrics, onClick, clickable }) {
    return (
        <div
            className={`detail-card detail-tone-${tone} ${clickable ? "detail-card-clickable" : ""}`}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onClick : undefined}
            onKeyDown={
                clickable
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onClick?.();
                          }
                      }
                    : undefined
            }
        >
            <div className="detail-card-head">
                <div className="detail-card-head-left">
                    <span className="detail-card-icon">{icon}</span>
                    <div>
                        <div className="detail-card-title" title={tooltip}>
                            {title}
                            {tooltip ? <FaInfoCircle className="ms-2 text-white opacity-75" style={{ fontSize: 11 }} /> : null}
                        </div>
                        {subtitle ? <div className="detail-card-sub">{subtitle}</div> : null}
                        {clickable ? (
                            <div className="detail-card-sub mt-1 opacity-90">
                                <FaExternalLinkAlt className="me-1" />
                                Click to view list
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="detail-card-body">
                <div className="detail-metrics">
                    {metrics.map((m) => (
                        <div key={m.label} className={`detail-metric ${m.wide ? "detail-metric-wide" : ""}`}>
                            <div className="detail-metric-label">{m.label}</div>
                            {m.hint ? <div className="detail-metric-hint">{m.hint}</div> : null}
                            <div className={`detail-metric-value ${m.valueClass || ""}`}>{m.value}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StatPill({ icon, label, value, tooltip, valueClass = "", onClick, clickable }) {
    return (
        <div
            className={`stat-pill ${clickable ? "stat-pill-clickable" : ""}`}
            title={clickable ? `${tooltip || label} — Click to view list` : tooltip}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onClick : undefined}
            onKeyDown={
                clickable
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onClick?.();
                          }
                      }
                    : undefined
            }
        >
            <div className="stat-pill-icon">{icon}</div>
            <div className="stat-pill-meta">
                <div className="stat-pill-label d-flex align-items-center gap-1">
                    {label}
                    {tooltip ? <FaInfoCircle className="text-muted" style={{ fontSize: 10 }} /> : null}
                </div>
                <div className={`stat-pill-value ${valueClass}`}>{value}</div>
            </div>
        </div>
    );
}

function isAllStatsZero(stats) {
    if (!stats) return true;
    const fd = Number(stats.first_deposit?.amount) || 0;
    const nd = Number(stats.normal_deposit?.amount) || 0;
    const wd = Number(stats.withdrawal?.amount) || 0;
    const tr = stats.trade || {};
    const tradeWin = Number(tr.win_amount) || 0;
    const tradeLoss = Number(tr.loss_amount) || 0;
    const nums = [
        fd,
        nd,
        fd + nd,
        wd,
        tr.amount,
        tradeWin,
        tradeLoss,
        tr.count,
        tradeWin - tradeLoss,
        wd - (fd + nd),
    ];
    return nums.every((v) => !Number(v));
}

/* ----------------- Page ----------------- */
export default function UserDownlineReport() {
    const { username: routeUsername } = useParams();
    const navigate = useNavigate();
    const { logout } = useAuth();

    const today = useMemo(() => toYMD(new Date()), []);
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [activePreset, setActivePreset] = useState("today");

    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [listModalVariant, setListModalVariant] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const openListModal = useCallback(
        (cardKey) => {
            const variant = CARD_MODAL_VARIANT[cardKey];
            if (!variant || !routeUsername?.trim()) return;
            setListModalVariant(variant);
        },
        [routeUsername]
    );

    const closeListModal = useCallback(() => setListModalVariant(null), []);

    const validateDates = useCallback(
        (from = fromDate, to = toDate) => {
            if (!routeUsername?.trim()) return "Invalid report link";
            if (!from || !to) return "Please select both From Date and To Date";
            if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return "From Date must be in YYYY-MM-DD format";
            if (!/^\d{4}-\d{2}-\d{2}$/.test(to)) return "To Date must be in YYYY-MM-DD format";
            if (from > today) return "From Date cannot be in the future";
            if (to > today) return "To Date cannot be in the future";
            if (from > to) return "From Date cannot be greater than To Date";
            return null;
        },
        [fromDate, toDate, routeUsername, today]
    );

    const onFromDateChange = (value) => {
        setActivePreset("");
        const next = clampToMaxDate(value, today);
        setFromDate(next);
        if (toDate && next > toDate) setToDate(next);
    };

    const onToDateChange = (value) => {
        setActivePreset("");
        const next = clampToMaxDate(value, today);
        setToDate(next);
        if (fromDate && next < fromDate) setFromDate(next);
    };

    const handleApiError = useCallback(
        (out) => {
            if (out?.error === "session expired") {
                toast.error("Session expired. Please login again.");
                logout();
                return true;
            }
            if (out?.error === true || (out?.error && out?.error !== false)) {
                const msg =
                    out?.message ||
                    (typeof out?.error === "string" ? out.error : "Failed to fetch leader report statistics");
                toast.error(msg);
                return true;
            }
            return false;
        },
        [logout]
    );

    const fetchReport = useCallback(
        async (overrides) => {
        const token = localStorage.getItem("token") || "";
        if (!token) {
            toast.error("Token not found. Please login again.");
            logout();
            return;
        }

        const from = overrides?.from_date ?? fromDate;
        const to = overrides?.to_date ?? toDate;

        const err = validateDates(from, to);
        if (err) {
            toast.error(err);
            return;
        }

        const username = routeUsername.trim();

        setLoading(true);
        try {
            const out = await fetchLeaderReportStatistics({
                token,
                username,
                from_date: from,
                to_date: to,
            });

            if (handleApiError(out)) {
                setReport(null);
                return;
            }

            if (out?.error !== false && !out?.data) {
                toast.error("Failed to fetch leader report statistics");
                setReport(null);
                return;
            }

            setReport(out.data);
        } catch (e) {
            console.log(e);
            toast.error("Failed to fetch leader report statistics");
            setReport(null);
        } finally {
            setLoading(false);
        }
    },
        [fromDate, toDate, routeUsername, logout, validateDates, handleApiError]
    );

    const applyPreset = useCallback(
        (key) => {
            const { from, to } = getDatePresetRange(key);
            const fromC = clampToMaxDate(from, today);
            const toC = clampToMaxDate(to, today);
            setActivePreset(key);
            setFromDate(fromC);
            setToDate(toC);
            fetchReport({ from_date: fromC, to_date: toC });
        },
        [today, fetchReport]
    );

    useEffect(() => {
        if (routeUsername) {
            fetchReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeUsername]);

    const profile = report?.profile;
    const stats = report?.statistics;
    const showEmpty = report && isAllStatsZero(stats);

    const mobileDisplay = useMemo(() => {
        const mobile = safeText(profile?.mobile);
        const cc = profile?.country_code;
        if (mobile === "—") return "—";
        const ccStr = cc === null || cc === undefined || cc === "" ? "" : String(cc);
        return `+${ccStr} ${mobile}`.trim();
    }, [profile]);

    const onSubmit = (e) => {
        e.preventDefault();
        fetchReport();
    };

    // data.statistics.* → displayed metrics
    const fd = stats?.first_deposit || {};
    const nd = stats?.normal_deposit || {};
    const wd = stats?.withdrawal || {};
    const tr = stats?.trade || {};
    const firstDepositAmt = Number(fd.amount) || 0;
    const normalDepositAmt = Number(nd.amount) || 0;
    const totalDepositAmt = firstDepositAmt + normalDepositAmt;
    const withdrawalAmt = Number(wd.amount) || 0;
    const tradeAmount = Number(tr.amount) || 0;
    const tradeWin = Number(tr.win_amount) || 0;
    const tradeLoss = Number(tr.loss_amount) || 0;
    const tradeCount = Number(tr.count) || 0;
    const tradeProfit = tradeWin - tradeLoss;
    const netProfit = withdrawalAmt - totalDepositAmt;

    const statCards = useMemo(
        () => [
            {
                key: "first_deposit",
                label: "First Deposit",
                value: nfmt2(firstDepositAmt),
                icon: <FaCoins />,
                tooltip: METRIC_TOOLTIPS.first_deposit,
                clickable: true,
            },
            {
                key: "normal_deposit",
                label: "Normal Deposit",
                value: nfmt2(normalDepositAmt),
                icon: <FaMoneyBillWave />,
                tooltip: METRIC_TOOLTIPS.normal_deposit,
                clickable: true,
            },
            {
                key: "total_deposit",
                label: "Total Deposit",
                value: nfmt2(totalDepositAmt),
                icon: <FaMoneyBillWave />,
                tooltip: "Sum of first deposit and normal deposit amounts.",
                clickable: false,
            },
            {
                key: "withdrawals",
                label: "Withdrawals",
                value: nfmt2(withdrawalAmt),
                icon: <FaExchangeAlt />,
                tooltip: METRIC_TOOLTIPS.withdrawal,
                clickable: true,
            },
            {
                key: "trade_amount",
                label: "Trade Amount",
                value: nfmt2(tradeAmount),
                icon: <FaChartBar />,
                tooltip: "Total downline trade volume.",
                clickable: true,
            },
            {
                key: "trade_win",
                label: "Trade Win",
                value: nfmt2(tradeWin),
                icon: <FaChartBar />,
                tooltip: "Settled win payout.",
                clickable: true,
            },
            {
                key: "trade_loss",
                label: "Trade Loss",
                value: nfmt2(tradeLoss),
                icon: <FaChartBar />,
                tooltip: "Loss stake.",
                clickable: true,
            },
            {
                key: "trade_count",
                label: "Trade Count",
                value: cfmt(tradeCount),
                icon: <FaChartBar />,
                tooltip: "Number of trades.",
                clickable: true,
            },
            {
                key: "net_profit",
                label: "Net Profit",
                value: nfmt2(netProfit),
                icon: <FaExchangeAlt />,
                tooltip: "Total withdrawal minus total deposit (first + normal).",
                valueClass: netProfit >= 0 ? "text-success" : netProfit < 0 ? "text-danger" : "",
                clickable: false,
            },
        ],
        [
            firstDepositAmt,
            normalDepositAmt,
            totalDepositAmt,
            withdrawalAmt,
            tradeAmount,
            tradeWin,
            tradeLoss,
            tradeCount,
            netProfit,
        ]
    );

    const detailCards = useMemo(
        () => [
            {
                key: "first_deposit",
                title: "First Deposit",
                tone: "green",
                icon: <FaCoins />,
                tooltip: METRIC_TOOLTIPS.first_deposit,
                subtitle: "Referral / first-deposit commission (levels 1–6)",
                metrics: [
                    { label: "Total Amount", value: nfmt2(fd.amount) },
                    { label: "Transactions", value: cfmt(fd.count), hint: "Record count" },
                    { label: "Distinct Users", value: cfmt(fd.user_count), hint: "Unique downlines" },
                ],
            },
            {
                key: "normal_deposit",
                title: "Normal Deposit",
                tone: "blue",
                icon: <FaMoneyBillWave />,
                tooltip: METRIC_TOOLTIPS.normal_deposit,
                subtitle: "USDT → coin swaps by downlines",
                metrics: [
                    { label: "Total Amount", value: nfmt2(nd.amount) },
                    { label: "Transactions", value: cfmt(nd.count), hint: "Record count" },
                    { label: "Distinct Users", value: cfmt(nd.user_count), hint: "Unique downlines" },
                ],
            },
            {
                key: "total_deposit_summary",
                title: "Total Deposits",
                tone: "green",
                icon: <FaCoins />,
                tooltip: "Combined first and normal deposit amounts.",
                subtitle: "First deposit + normal deposit",
                metrics: [
                    { label: "First Deposit", value: nfmt2(fd.amount) },
                    { label: "Normal Deposit", value: nfmt2(nd.amount) },
                    {
                        label: "Total Deposit",
                        value: nfmt2(totalDepositAmt),
                        hint: "First + normal",
                        wide: true,
                    },
                ],
            },
            {
                key: "withdrawal",
                title: "Withdrawals",
                tone: "red",
                icon: <FaExchangeAlt />,
                tooltip: METRIC_TOOLTIPS.withdrawal,
                subtitle: "Coin → USDT swaps by downlines",
                metrics: [
                    { label: "Total Amount", value: nfmt2(wd.amount) },
                    { label: "Transactions", value: cfmt(wd.count), hint: "Record count" },
                    { label: "Distinct Users", value: cfmt(wd.user_count), hint: "Unique downlines" },
                ],
            },
            {
                key: "trade",
                title: "Trades",
                tone: "amber",
                icon: <FaChartBar />,
                tooltip: METRIC_TOOLTIPS.trade,
                subtitle: "Downline trading activity in period",
                metrics: [
                    { label: "Trade Volume", value: nfmt2(tr.amount), hint: "Total amount" },
                    { label: "Trade Count", value: cfmt(tr.count), hint: "All trades" },
                    { label: "Distinct Users", value: cfmt(tr.user_count), hint: "Unique downlines" },
                    { label: "Win Amount", value: nfmt2(tr.win_amount), hint: "Settled wins" },
                    { label: "Loss Amount", value: nfmt2(tr.loss_amount), hint: "Loss stake" },
                    {
                        label: "Trade P/L",
                        value: nfmt2(tradeProfit),
                        hint: "Win − loss",
                        valueClass: tradeProfit >= 0 ? "text-success" : tradeProfit < 0 ? "text-danger" : "",
                    },
                ],
            },
        ],
        [fd, nd, wd, tr, tradeProfit, totalDepositAmt]
    );

    const downloadPdf = useCallback(async () => {
        const err = validateDates();
        if (err) {
            toast.error(err);
            return;
        }
        if (!report?.profile) {
            toast.error("Report not loaded.");
            return;
        }

        setDownloading(true);
        try {
            const doc = new jsPDF({ unit: "pt", format: "a4" });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const marginX = 48;
            const marginRight = 48;
            const contentW = pageWidth - marginX - marginRight;
            const SECTION_GAP = 24;
            const TABLE_GAP = 16;
            const bottomLimit = pageHeight - 40;

            const COLORS = {
                header: [15, 23, 42],
                headerAccent: [13, 110, 253],
                primary: [13, 110, 253],
                purple: [111, 66, 193],
                teal: [13, 148, 136],
                green: [25, 135, 84],
                red: [220, 53, 69],
                amber: [217, 119, 6],
                muted: [100, 116, 139],
                cardBg: [248, 250, 252],
                stripe: [241, 245, 249],
                border: [226, 232, 240],
                profit: [25, 135, 84],
                loss: [220, 53, 69],
            };

            const CARD_HEADER_COLORS = {
                first_deposit: COLORS.green,
                normal_deposit: COLORS.primary,
                total_deposit_summary: COLORS.teal,
                withdrawal: COLORS.red,
            };

            const periodFrom = ymdToDdMmYyyy(fromDate);
            const periodTo = ymdToDdMmYyyy(toDate);
            const profileId = String(report.profile?.id ?? "report").replace(/[^\w-]+/g, "");

            let y = 0;

            const ensureSpace = (needed) => {
                if (y + needed > bottomLimit) {
                    doc.addPage();
                    y = 56;
                }
            };

            const sectionGap = () => {
                y += SECTION_GAP;
            };

            const drawBanner = () => {
                doc.setFillColor(...COLORS.header);
                doc.rect(0, 0, pageWidth, 82, "F");
                doc.setFillColor(...COLORS.headerAccent);
                doc.rect(0, 78, pageWidth, 4, "F");

                doc.setTextColor(255, 255, 255);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(22);
                doc.text("Downline Report", marginX, 38);

                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                doc.text(`Reporting period: ${periodFrom}  –  ${periodTo}`, marginX, 58);
                doc.setTextColor(0);
                return 98;
            };

            const drawSectionTitle = (title, accentRgb) => {
                ensureSpace(36);
                sectionGap();
                const barH = 26;
                doc.setFillColor(...accentRgb);
                doc.roundedRect(marginX, y, contentW, barH, 4, 4, "F");
                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.setTextColor(255, 255, 255);
                doc.text(title, marginX + 12, y + 17);
                doc.setTextColor(0);
                y += barH + TABLE_GAP;
            };

            const addKeyValTable = (rows, headRgb, options = {}) => {
                const { highlightLastRow = false, lastRowValueColor = null } = options;
                ensureSpace(40 + rows.length * 22);
                autoTable(doc, {
                    startY: y,
                    theme: "plain",
                    head: [["Metric", "Amount"]],
                    body: rows,
                    styles: {
                        fontSize: 9.5,
                        cellPadding: { top: 8, right: 10, bottom: 8, left: 10 },
                        lineColor: COLORS.border,
                        lineWidth: 0.4,
                        textColor: [30, 41, 59],
                    },
                    headStyles: {
                        fillColor: headRgb,
                        textColor: 255,
                        fontStyle: "bold",
                        halign: "left",
                    },
                    alternateRowStyles: { fillColor: COLORS.stripe },
                    columnStyles: {
                        0: { cellWidth: contentW * 0.55 },
                        1: { halign: "right", fontStyle: "bold" },
                    },
                    margin: { left: marginX, right: marginRight },
                    tableWidth: contentW,
                    didParseCell: (data) => {
                        if (!highlightLastRow || data.section !== "body") return;
                        const isLast = data.row.index === rows.length - 1;
                        if (!isLast) return;
                        if (data.column.index === 0) {
                            data.cell.styles.fontStyle = "normal";
                            data.cell.styles.fontSize = 9.5;
                        } else if (data.column.index === 1) {
                            data.cell.styles.fontStyle = "bold";
                            data.cell.styles.fontSize = 10;
                            if (lastRowValueColor) data.cell.styles.textColor = lastRowValueColor;
                        }
                    },
                });
                y = doc.lastAutoTable.finalY + TABLE_GAP;
            };

            const drawProfileCard = () => {
                ensureSpace(100);
                sectionGap();
                drawSectionTitle("Profile", COLORS.purple);

                const rows = [
                    ["Name", safeText(report.profile?.name)],
                    ["ID", safeText(report.profile?.id)],
                    ["Mobile", mobileDisplay],
                    ["Email", safeText(report.profile?.email)],
                ];
                autoTable(doc, {
                    startY: y,
                    theme: "plain",
                    body: rows,
                    styles: {
                        fontSize: 10,
                        cellPadding: { top: 7, right: 10, bottom: 7, left: 10 },
                        fillColor: COLORS.cardBg,
                        textColor: [30, 41, 59],
                        lineColor: COLORS.border,
                        lineWidth: 0.35,
                    },
                    columnStyles: {
                        0: { fontStyle: "bold", cellWidth: 90, textColor: COLORS.muted },
                        1: { halign: "left" },
                    },
                    margin: { left: marginX, right: marginRight },
                    tableWidth: contentW,
                });
                y = doc.lastAutoTable.finalY + TABLE_GAP;
            };

            y = drawBanner();
            drawProfileCard();

            drawSectionTitle("Summary (Downline levels 1–6)", COLORS.primary);
            const netProfitColor = netProfit >= 0 ? COLORS.profit : netProfit < 0 ? COLORS.loss : [30, 41, 59];
            addKeyValTable(
                [
                    ["First Deposit", nfmt2(firstDepositAmt)],
                    ["Normal Deposit", nfmt2(normalDepositAmt)],
                    ["Total Deposit", nfmt2(totalDepositAmt)],
                    ["Withdrawals", nfmt2(withdrawalAmt)],
                    ["Net Profit", nfmt2(netProfit)],
                ],
                COLORS.primary,
                { highlightLastRow: true, lastRowValueColor: netProfitColor }
            );

            drawSectionTitle("Activity Details", COLORS.teal);
            detailCards
                .filter((card) => card.key !== "trade")
                .forEach((card) => {
                    const headColor = CARD_HEADER_COLORS[card.key] || COLORS.teal;
                    ensureSpace(80);
                    y += 10;
                    doc.setFillColor(...headColor);
                    doc.roundedRect(marginX, y, contentW, 22, 3, 3, "F");
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(10);
                    doc.setTextColor(255, 255, 255);
                    doc.text(card.title, marginX + 10, y + 15);
                    doc.setTextColor(0);
                    y += 30;

                    autoTable(doc, {
                        startY: y,
                        theme: "plain",
                        head: [["Metric", "Value"]],
                        body: card.metrics.map((m) => [pdfMetricLabel(m.label), m.value]),
                        styles: {
                            fontSize: 9.5,
                            cellPadding: { top: 7, right: 10, bottom: 7, left: 10 },
                            lineColor: COLORS.border,
                            lineWidth: 0.35,
                            textColor: [30, 41, 59],
                        },
                        headStyles: {
                            fillColor: headColor,
                            textColor: 255,
                            fontStyle: "bold",
                        },
                        alternateRowStyles: { fillColor: COLORS.stripe },
                        columnStyles: {
                            0: { cellWidth: contentW * 0.55 },
                            1: { halign: "right", fontStyle: "bold" },
                        },
                        margin: { left: marginX, right: marginRight },
                        tableWidth: contentW,
                    });
                    y = doc.lastAutoTable.finalY + SECTION_GAP;
                });

            const pdfFilename = `${profileId}_${ymdToFilenameDate(fromDate)}_${ymdToFilenameDate(toDate)}.pdf`;
            doc.save(pdfFilename);
            toast.success("PDF downloaded.");
        } catch (e) {
            console.error(e);
            toast.error("Failed to generate PDF.");
        } finally {
            setDownloading(false);
        }
    }, [
        validateDates,
        report,
        mobileDisplay,
        fromDate,
        toDate,
        firstDepositAmt,
        normalDepositAmt,
        totalDepositAmt,
        withdrawalAmt,
        netProfit,
        detailCards,
    ]);

    return (
        <PageTransition>
            <style>{`
        .hero {
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(13,110,253,.10), rgba(32,201,151,.08));
          border: 1px solid rgba(15,23,42,.08);
          box-shadow: 0 10px 34px rgba(15,23,42,.08);
        }
        .filters-card {
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,.08);
          box-shadow: 0 10px 34px rgba(15,23,42,.08);
        }
        .stat-pill{
          display:flex;
          gap: 12px;
          padding: 14px;
          align-items:flex-start;
        }
        .stat-pill-icon{
          width: 44px;
          height: 44px;
          border-radius: 14px;
          display:flex;
          align-items:center;
          justify-content:center;
          background: rgba(13,110,253,.10);
          color:#0d6efd;
          flex: 0 0 auto;
          border: 1px solid rgba(13,110,253,.15);
        }
        .stat-pill-label{
          font-size: 12px;
          color: rgba(15,23,42,.65);
          font-weight: 700;
        }
        .stat-pill-value{
          margin-top: 4px;
          font-size: 18px;
          font-weight: 900;
          color: rgba(15,23,42,.92);
        }
        .stat-pill-clickable{
          cursor:pointer;
          border-radius:14px;
          transition:transform .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .stat-pill-clickable:hover{
          background:rgba(13,110,253,.06);
          transform:translateY(-2px);
        }
        .summary-card-clickable{
          cursor:pointer;
          transition:transform .15s ease, box-shadow .15s ease;
        }
        .summary-card-clickable:hover{
          transform:translateY(-2px);
          box-shadow:0 14px 40px rgba(15,23,42,.12);
        }
        .detail-card-clickable{
          cursor:pointer;
          transition:transform .15s ease, box-shadow .15s ease;
        }
        .detail-card-clickable:hover{
          transform:translateY(-2px);
          box-shadow:0 16px 42px rgba(15,23,42,.12);
        }
        .summary-grid{
          display:grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 12px;
        }
        .summary-card{
          grid-column: span 4;
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,.08);
          box-shadow: 0 10px 34px rgba(15,23,42,.08);
          background: #fff;
        }
        @media (max-width: 992px){ .summary-card{ grid-column: span 6; } }
        @media (max-width: 576px){ .summary-card{ grid-column: span 12; } }
        .stats-section-title{
          font-weight: 900;
          color: rgba(15,23,42,.92);
          margin-bottom: 12px;
        }
        .empty-state{
          border-radius: 14px;
          border: 1px dashed rgba(15,23,42,.15);
          background: rgba(15,23,42,.02);
        }
        .profile-card {
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 10px 34px rgba(15,23,42,.10);
          border: 1px solid rgba(15,23,42,.08);
        }
        .profile-avatar {
          width: 72px;
          height: 72px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          background: rgba(13,110,253,.12);
          color: #0d6efd;
        }
        .profile-top { padding: 16px; background: rgba(255,255,255,.75); }
        .profile-meta {
          border-top: 1px dashed rgba(15,23,42,.12);
          padding-top: 14px;
          margin-top: 14px;
        }
        .preset-wrap{
          display:flex;
          flex-wrap:wrap;
          gap:8px;
        }
        .preset-btn{
          border-radius:999px;
          font-size:12px;
          font-weight:700;
          padding:6px 14px;
        }
        .detail-card{
          border-radius:16px;
          overflow:hidden;
          border:1px solid rgba(15,23,42,.08);
          box-shadow:0 12px 36px rgba(15,23,42,.08);
          background:#fff;
          height:100%;
        }
        .detail-card-head{
          padding:16px 18px;
          color:#fff;
        }
        .detail-card-head-left{
          display:flex;
          align-items:center;
          gap:14px;
        }
        .detail-card-icon{
          width:48px;height:48px;
          border-radius:14px;
          display:flex;align-items:center;justify-content:center;
          background:rgba(255,255,255,.2);
          font-size:20px;
          flex-shrink:0;
        }
        .detail-card-title{
          font-weight:900;
          font-size:16px;
          display:flex;
          align-items:center;
        }
        .detail-card-sub{
          font-size:12px;
          opacity:.88;
          margin-top:4px;
          font-weight:600;
        }
        .detail-tone-green .detail-card-head{
          background:linear-gradient(135deg,#198754,#20c997);
        }
        .detail-tone-blue .detail-card-head{
          background:linear-gradient(135deg,#0d6efd,#6ea8fe);
        }
        .detail-tone-red .detail-card-head{
          background:linear-gradient(135deg,#dc3545,#f08080);
        }
        .detail-tone-amber .detail-card-head{
          background:linear-gradient(135deg,#b58100,#ffc107);
          color:rgba(15,23,42,.92);
        }
        .detail-tone-amber .detail-card-icon{
          background:rgba(15,23,42,.12);
        }
        .detail-card-body{ padding:16px 18px 18px; }
        .detail-metrics{
          display:grid;
          grid-template-columns:repeat(2,1fr);
          gap:12px;
        }
        .detail-metric{
          border:1px solid rgba(15,23,42,.08);
          border-radius:12px;
          padding:12px 14px;
          background:rgba(15,23,42,.02);
        }
        .detail-metric-wide{ grid-column:1/-1; }
        .detail-metric-label{
          font-size:11px;
          font-weight:800;
          text-transform:uppercase;
          letter-spacing:.4px;
          color:rgba(15,23,42,.55);
        }
        .detail-metric-hint{
          font-size:11px;
          color:rgba(15,23,42,.45);
          margin-top:2px;
        }
        .detail-metric-value{
          margin-top:8px;
          font-size:20px;
          font-weight:900;
          color:rgba(15,23,42,.92);
          line-height:1.1;
        }
      `}</style>

            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                <div>
                    <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                        <button
                            type="button"
                            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                            onClick={() => navigate(-1)}
                            style={{ padding: "0.5rem 1rem" }}
                        >
                            <FaArrowLeft /> Back
                        </button>
                        <h2 className="fw-bold mb-0">Leader Report — Statistics</h2>
                    </div>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                    {routeUsername ? (
                        <button
                            type="button"
                            className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                            onClick={() => navigate(`/leader-stats/${encodeURIComponent(routeUsername)}`)}
                            style={{ padding: "0.5rem 1rem" }}
                        >
                            <FaChartBar /> Team Report
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="btn btn-outline-danger d-inline-flex align-items-center gap-2"
                        onClick={downloadPdf}
                        disabled={downloading || !report?.profile}
                        title="Download PDF (deposits & withdrawals only)"
                        style={{ padding: "0.5rem 1rem" }}
                    >
                        <FaFilePdf />
                        {downloading ? "Generating..." : "Download PDF"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={fetchReport}
                        disabled={loading}
                        style={{ padding: "0.5rem 1rem" }}
                    >
                        <FaSyncAlt className={loading ? "tb-spin" : ""} />
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                </div>
            </div>

            <div className="card filters-card mb-4">
                <div className="card-body">
                    <form onSubmit={onSubmit}>
                        <div className="mb-3">
                            <div className="form-label small text-muted mb-2">Quick range</div>
                            <div className="preset-wrap">
                                {DATE_PRESETS.map((p) => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        className={`btn preset-btn ${
                                            activePreset === p.key ? "btn-primary" : "btn-outline-secondary"
                                        }`}
                                        onClick={() => applyPreset(p.key)}
                                        disabled={loading}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="row g-3 align-items-end">
                            <div className="col-12 col-md-5">
                                <label className="form-label small text-muted mb-1">
                                    <FaCalendarAlt className="me-2" />
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={fromDate}
                                    max={today}
                                    onChange={(e) => onFromDateChange(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-12 col-md-5">
                                <label className="form-label small text-muted mb-1">
                                    <FaCalendarAlt className="me-2" />
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={toDate}
                                    max={today}
                                    onChange={(e) => onToDateChange(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-12 col-md-2">
                                <button
                                    type="submit"
                                    className="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center gap-2"
                                    disabled={loading}
                                >
                                    <FaSearch />
                                    {loading ? "..." : "Apply"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {loading && !report ? (
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
                    <div className="card-body text-center py-5">
                        <div className="spinner-border text-primary mb-3" role="status" />
                        <div className="text-muted">Loading downline statistics…</div>
                    </div>
                </div>
            ) : null}

            {report ? (
                <>
                    <div className="card profile-card mb-4 hero border-0">
                        <div className="card-body profile-top">
                            {!profile ? (
                                <div className="text-center text-muted py-4">
                                    <div className="profile-avatar mb-3">
                                        <FaUserCircle size={34} />
                                    </div>
                                    <div className="fw-bold">No profile data available</div>
                                </div>
                            ) : (
                                <div className="row g-3 align-items-center">
                                    <div className="col-12 col-md-3 text-center">
                                        <div className="profile-avatar mb-2">
                                            <FaUserCircle size={34} />
                                        </div>
                                        <div className="fw-bold fs-5">{safeText(profile.name)}</div>
                                        <div className="text-muted small mt-1 d-flex align-items-center justify-content-center gap-1">
                                            <FaIdCard />
                                            ID {safeText(profile.id)}
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-5">
                                        <div className="row g-3">
                                            <div className="col-12 col-sm-6">
                                                <div className="d-flex align-items-center gap-2 text-muted small">
                                                    <FaPhoneAlt className="text-primary" /> Mobile
                                                </div>
                                                <div className="fw-semibold">{mobileDisplay}</div>
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <div className="d-flex align-items-center gap-2 text-muted small">
                                                    <FaEnvelope className="text-primary" /> Email
                                                </div>
                                                <div className="fw-semibold" style={{ wordBreak: "break-word" }}>
                                                    {safeText(profile.email)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-4">
                                        <div className="profile-meta row g-2 g-md-3 mb-0">
                                            <div className="col-6">
                                                <div className="text-muted small d-flex align-items-center gap-2">
                                                    <FaWallet className="text-primary" /> Total Coin
                                                </div>
                                                <div className="fw-semibold">
                                                    {nfmt2(profile.balance?.total_coin)}
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-muted small d-flex align-items-center gap-2">
                                                    <FaWallet className="text-primary" /> Locked Coin
                                                </div>
                                                <div className="fw-semibold">
                                                    {nfmt2(profile.balance?.lock_coin)}
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-muted small d-flex align-items-center gap-2">
                                                    <FaCoins className="text-primary" /> Total USDT
                                                </div>
                                                <div className="fw-semibold">
                                                    {nfmt2(profile.balance?.total_usdt)}
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-muted small d-flex align-items-center gap-2">
                                                    <FaCoins className="text-primary" /> Locked USDT
                                                </div>
                                                <div className="fw-semibold">
                                                    {nfmt2(profile.balance?.lock_usdt)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {showEmpty ? (
                        <div className="empty-state text-center py-5 mb-4">
                            <FaChartBar size={32} className="text-muted mb-3" />
                            <div className="fw-bold">No activity in this period</div>
                            <div className="text-muted small mt-1">
                                All downline statistics are zero for the selected date range.
                            </div>
                        </div>
                    ) : null}

                    <div className="mb-4">
                        <div className="stats-section-title d-flex align-items-center gap-2">
                            <FaChartBar className="text-primary" />
                            Statistics
                        </div>
                        <div className="summary-grid">
                            {statCards.map((card) => (
                                <div
                                    key={card.key}
                                    className={`summary-card ${card.clickable ? "summary-card-clickable" : ""}`}
                                >
                                    <StatPill
                                        icon={card.icon}
                                        label={card.label}
                                        value={card.value}
                                        tooltip={card.tooltip}
                                        valueClass={card.valueClass}
                                        clickable={card.clickable}
                                        onClick={() => openListModal(card.key)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mb-3">
                        <div className="stats-section-title d-flex align-items-center gap-2">
                            <FaListAlt className="text-primary" />
                            Activity Details
                        </div>
                    </div>
                    <div className="row g-4 mb-4">
                        {detailCards.map((card) => (
                            <div key={card.key} className="col-12 col-lg-6">
                                <DetailCard
                                    title={card.title}
                                    tone={card.tone}
                                    icon={card.icon}
                                    tooltip={card.tooltip}
                                    subtitle={card.subtitle}
                                    metrics={card.metrics}
                                    clickable={!!CARD_MODAL_VARIANT[card.key]}
                                    onClick={() => openListModal(card.key)}
                                />
                            </div>
                        ))}
                    </div>
                </>
            ) : !loading ? (
                <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
                    <div className="card-body text-center py-5 text-muted">
                        <FaUserCircle size={36} className="mb-3" />
                        <div className="fw-bold">No report loaded</div>
                        <div className="small mt-1">Choose a date range and click Apply.</div>
                    </div>
                </div>
            ) : null}

            <LeaderReportListModal
                open={!!listModalVariant}
                variant={listModalVariant}
                onClose={closeListModal}
                leaderUsername={routeUsername || ""}
                fromDate={fromDate}
                toDate={toDate}
                onSessionExpired={() => {
                    closeListModal();
                    logout();
                }}
            />
        </PageTransition>
    );
}
